import { Stack, Typography } from "@mui/material";
import { AlertsContext } from "@sista/dista-ui";
import {
  ProfileRepresentation,
  Right,
  SistaDialog,
  SmartButton,
  UiContext,
  useRequiredValidation,
  useSafeSistaAsync,
} from "@sista/library-stasi";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  DateTimePickerElement,
  FormContainer,
  SelectElement,
} from "react-hook-form-mui";
import { useTranslation } from "react-i18next";

import { useRejstrikClient } from "../../../api/utils/useRejstrikClient";
import { urnEntityId } from "../../../utils/representation";

type ChangeRepresentationForm = {
  substituteProfileId?: string;
  dateFrom: Date;
  dateTo: Date;
};

type ChangeRepresentationDialogProps = {
  representation: ProfileRepresentation;
  onDone: () => void;
};

/** Spravce zmeni zastupce nebo termin zastupu (jiny zastupce = stary zastup skonci, novy zacne nejdriv dnes). */
export const ChangeRepresentationDialog = (
  props: ChangeRepresentationDialogProps
) => {
  const { closeDialogs } = useContext(UiContext);
  const { showSuccess } = useContext(AlertsContext);
  const { t } = useTranslation("ucet");
  const required = useRequiredValidation();
  const rej = useRejstrikClient();
  const profileId = urnEntityId(props.representation.profileUrn);

  const [candidatesCall, candidatesState] = useSafeSistaAsync(
    rej.findRepresentationCandidates
  );
  useEffect(() => {
    candidatesCall({ profileId });
  }, [candidatesCall, profileId]);

  const options = useMemo(
    () =>
      (candidatesState.value ?? []).map((c) => ({
        id: urnEntityId(c.profileUrn),
        label: c.displayName ?? c.profileUrn,
        personUrn: c.personUrn,
      })),
    [candidatesState.value]
  );

  const form = useForm<ChangeRepresentationForm>({
    defaultValues: {
      dateFrom: new Date(props.representation.from),
      dateTo: props.representation.to
        ? new Date(props.representation.to)
        : undefined,
    },
  });

  // zastupce se predvybere podle osoby, kandidati jsou jeho profily u poskytovatele
  useEffect(() => {
    const current = options.find(
      (o) => o.personUrn === props.representation.substitutePersonUrn
    );
    if (current && !form.getValues("substituteProfileId")) {
      form.setValue("substituteProfileId", current.id);
    }
  }, [options, form, props.representation.substitutePersonUrn]);

  const [changeCall, changeState] = useSafeSistaAsync(
    useCallback(
      async (data: ChangeRepresentationForm) => {
        const result = await rej.changeRepresentation({
          profileId,
          substituteProfileId: data.substituteProfileId!,
          dateFrom: data.dateFrom.toISOString(),
          dateTo: data.dateTo.toISOString(),
        });
        showSuccess(t("ucet.representation.changed", "Zástup byl změněn"));
        props.onDone();
        closeDialogs();
        return result;
      },
      [rej, profileId, showSuccess, t, props, closeDialogs]
    )
  );

  return (
    <SistaDialog
      title={t("ucet.representation.changeTitle", "Změnit zástup")}
      onClose={closeDialogs}
    >
      <Typography variant="body1" sx={{ mb: 2 }}>
        {t(
          "ucet.representation.changeDescription",
          "Zástup za {{name}}. Při změně zástupce dosavadní zástup skončí a nový začne nejdříve dnes. Zastupovaný i zástupci dostanou e-mail.",
          { name: props.representation.profileDisplayName }
        )}
      </Typography>
      <FormContainer onSuccess={changeCall} formContext={form}>
        <Stack direction="column" spacing={2}>
          <SelectElement
            name="substituteProfileId"
            label={t("ucet.representation.substitute", "Zástupce")}
            options={options}
            validation={{ ...required }}
          />
          <DateTimePickerElement
            name="dateFrom"
            label={t("ucet.representation.from", "Od")}
            validation={{ ...required }}
          />
          <DateTimePickerElement
            name="dateTo"
            label={t("ucet.representation.to", "Do")}
            validation={{ ...required }}
          />
          <Right sx={{ marginTop: 4 }}>
            <SmartButton state={changeState}>
              {t("ucet.representation.save", "Uložit změnu")}
            </SmartButton>
          </Right>
        </Stack>
      </FormContainer>
    </SistaDialog>
  );
};
